import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';

/**
 * Bootstrap-tokens manager for a single node.
 *
 * What an admin does here:
 *   1. Sees the list of recent tokens (active / consumed / expired / revoked).
 *   2. Generates a new one with a chosen TTL — the raw token value is
 *      shown ONCE in a confirmation pane with copy + warning.
 *   3. Revokes any still-active token.
 *
 * The raw token is NEVER persisted to client state beyond what the
 * "show once" pane needs; navigating away or closing the modal drops it.
 */

const STATE_META = {
  active:   { tone: 'border-emerald-200 bg-emerald-50  text-emerald-700', label: 'Active'   },
  consumed: { tone: 'border-sky-200     bg-sky-50      text-sky-700',     label: 'Consumed' },
  expired:  { tone: 'border-amber-200   bg-amber-50    text-amber-700',   label: 'Expired'  },
  revoked:  { tone: 'border-rose-200    bg-rose-soft   text-rose-700',    label: 'Revoked'  },
};

const TTL_OPTIONS = [
  { value: 15,   label: '15 minutes' },
  { value: 60,   label: '1 hour (default)' },
  { value: 240,  label: '4 hours' },
  { value: 1440, label: '24 hours' },
];

export function BootstrapTokensModal({ open, node, viewerTimezone, onClose }) {
  const toast = useToast();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ttlMinutes, setTtlMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [showOnce, setShowOnce] = useState(null);   // { token, record } shown after Issue
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const reload = useCallback(async () => {
    if (!node) return;
    setLoading(true);
    try {
      const r = await api.nodes.bootstrapTokens.list(node.id);
      setTokens(r.tokens || []);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, [node]);

  useEffect(() => {
    if (open) {
      setShowOnce(null);
      setError(null);
      reload();
    }
  }, [open, reload]);

  const handleIssue = useCallback(async () => {
    if (!node) return;
    setBusy(true);
    try {
      const r = await api.nodes.bootstrapTokens.issue(node.id, ttlMinutes);
      setShowOnce({ token: r.token, record: r.record });
      reload();
    } catch (err) {
      toast.show({
        kind: 'error',
        message: err?.payload?.error || err.message || 'Failed to issue token.',
      });
    } finally {
      setBusy(false);
    }
  }, [node, ttlMinutes, reload, toast]);

  const handleRevoke = useCallback(async () => {
    if (!confirmRevoke) return;
    try {
      await api.nodes.bootstrapTokens.revoke(node.id, confirmRevoke.id);
      toast.show({ kind: 'success', message: `Token #${confirmRevoke.id} revoked.` });
      setConfirmRevoke(null);
      reload();
    } catch (err) {
      toast.show({
        kind: 'error',
        message: err?.payload?.error || err.message || 'Revoke failed.',
      });
    }
  }, [confirmRevoke, node, reload, toast]);

  const sortedTokens = useMemo(() => tokens, [tokens]);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={`Bootstrap tokens — ${node?.name || ''}`}
        description={
          showOnce
            ? 'Save this token NOW — it cannot be retrieved later.'
            : 'One-shot tokens for ad-tms-service self-enrollment. Each token can be used exactly once.'
        }
        size="xl"
      >
        {error && (
          <div role="alert" className="mb-4 rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        {/* "Show-once" pane after a fresh issue */}
        {showOnce ? (
          <ShowOncePane
            token={showOnce.token}
            record={showOnce.record}
            node={node}
            viewerTimezone={viewerTimezone}
            onClose={() => setShowOnce(null)}
          />
        ) : (
          <>
            {/* Issue form */}
            <div className="mb-5 rounded-2xl border border-line bg-canvas/40 px-5 py-4">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label htmlFor="ttl" className="field-label">Token validity</label>
                  <select
                    id="ttl"
                    className="field"
                    value={ttlMinutes}
                    onChange={(e) => setTtlMinutes(Number(e.target.value))}
                    disabled={busy}
                  >
                    {TTL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-mist">
                    After this window the token cannot be consumed even if not yet used.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleIssue}
                  disabled={busy}
                  className="btn-primary"
                >
                  {busy
                    ? <><span className="loader-spinner" aria-hidden="true" /> Issuing…</>
                    : <><i className="fa-solid fa-key" aria-hidden="true" /> Issue token</>}
                </button>
              </div>
            </div>

            {/* Token history */}
            <div className="card overflow-hidden">
              <header className="flex items-center justify-between border-b border-line bg-canvas/40 px-5 py-2 text-[11px] text-mist">
                <span>{loading ? 'Loading…' : `${sortedTokens.length} token(s)`}</span>
                <button type="button" className="hover:text-accent" onClick={reload}>
                  <i className="fa-solid fa-arrows-rotate mr-1" aria-hidden="true" /> Refresh
                </button>
              </header>
              {sortedTokens.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-mist">No tokens issued yet.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
                      <th className="w-20 px-5 py-2.5">ID</th>
                      <th className="px-5 py-2.5">State</th>
                      <th className="px-5 py-2.5">Created</th>
                      <th className="px-5 py-2.5">Expires / Consumed</th>
                      <th className="w-24 px-5 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTokens.map((t) => {
                      const meta = STATE_META[t.state] || STATE_META.active;
                      const subline = (() => {
                        if (t.state === 'consumed') return `consumed ${formatUtcInTimezone(t.consumedAt, viewerTimezone)}${t.consumedIp ? ` from ${t.consumedIp}` : ''}`;
                        if (t.state === 'revoked')  return `revoked ${formatUtcInTimezone(t.revokedAt, viewerTimezone)}${t.revokedBy ? ` by ${t.revokedBy}` : ''}`;
                        return `expires ${formatUtcInTimezone(t.expiresAt, viewerTimezone)}`;
                      })();
                      return (
                        <tr key={t.id} className="border-b border-line last:border-0 hover:bg-canvas/40">
                          <td className="px-5 py-2.5 font-mono text-mist">#{t.id}</td>
                          <td className="px-5 py-2.5">
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-5 py-2.5 text-xs text-mist">
                            {formatUtcInTimezone(t.createdAt, viewerTimezone)}
                            <div className="text-[10px] text-mist">{t.createdBy ? `by ${t.createdBy}` : ''}</div>
                          </td>
                          <td className="px-5 py-2.5 text-xs text-mist">{subline}</td>
                          <td className="px-5 py-2.5 text-right">
                            {t.state === 'active' && (
                              <button
                                type="button"
                                onClick={() => setConfirmRevoke(t)}
                                title="Revoke"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-xl text-mist hover:bg-rose-soft hover:text-rose-600"
                              >
                                <i className="fa-solid fa-ban" aria-hidden="true" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmRevoke}
        onClose={() => setConfirmRevoke(null)}
        onConfirm={handleRevoke}
        title={confirmRevoke ? `Revoke bootstrap token #${confirmRevoke.id}?` : ''}
        message="The token will no longer be accepted by /api/nodes/:id/bootstrap. If the operator already used it on the target VM, the existing certificate is unaffected."
        confirmLabel="Revoke"
        confirmTone="danger"
      />
    </>
  );
}

function ShowOncePane({ token, record, node, viewerTimezone, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // Pre-built .env snippet — operator pastes it on the target VM.
  const envSnippet =
`TMS_SERVER_URL=https://tms.micro-gis.com
NODE_ID=${node?.id || ''}
NODE_NAME=${node?.name || ''}
NODE_TIER=${node?.tier || ''}
BOOTSTRAP_TOKEN=${token}`;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-warning bg-warning-soft px-4 py-3 text-sm text-warning">
        <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
        <strong>Save this token now.</strong> It is not stored on the server in
        recoverable form — once you close this view it cannot be retrieved.
      </div>

      <div>
        <label className="field-label">Raw token</label>
        <div className="flex items-stretch gap-2">
          <code className="block flex-1 break-all rounded-2xl border border-line bg-canvas/40 px-4 py-3 font-mono text-[13px] text-ink">
            {token}
          </code>
          <button
            type="button"
            onClick={copy}
            className="btn-secondary"
            title="Copy"
          >
            <i className={`fa-solid ${copied ? 'fa-check text-emerald-600' : 'fa-copy'}`} aria-hidden="true" />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div>
        <label className="field-label">Drop-in <code className="font-mono">.env</code> snippet</label>
        <pre className="rounded-2xl border border-line bg-ink/95 px-4 py-3 text-[12px] leading-relaxed text-emerald-200 overflow-auto">
          {envSnippet}
        </pre>
        <p className="mt-1 text-xs text-mist">
          Paste into <code className="font-mono">/opt/adms/ad-tms-service/.env</code> on the target VM
          and run <code className="font-mono">systemctl restart ad-tms-service</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-line bg-canvas/40 px-4 py-2 text-xs">
          <div className="text-mist">Token ID</div>
          <div className="font-mono text-ink">#{record.id}</div>
        </div>
        <div className="rounded-xl border border-line bg-canvas/40 px-4 py-2 text-xs">
          <div className="text-mist">Expires</div>
          <div className="font-mono text-ink">
            {formatUtcInTimezone(record.expiresAt, viewerTimezone)}
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button type="button" onClick={onClose} className="btn-primary">
          <i className="fa-solid fa-check" aria-hidden="true" />
          I've saved it
        </button>
      </div>
    </div>
  );
}
